/** SUNTRONIX ONE — My Settings (personal preferences). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { own, isTenantUser } from "@/lib/terminology";

const DENSITIES = ["comfortable", "compact"];
const DATE_FORMATS = ["DD/MM/YYYY", "DD MMM YYYY", "YYYY-MM-DD", "MM/DD/YYYY"];
const TIME_FORMATS = ["24h", "12h"];
const LANDINGS = ["/my-one", "/sales-desk", "/operations",
                                    "/technology", "/administration", "/commercial/crm"];

export default function MySettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config-studio/my-settings").then(({ data }) => setForm(data || {}))
        .catch(() => toast.error("Load failed"));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.user_id; delete payload.email;
      delete payload.updated_at;
      if (payload.rows_per_page)
        payload.rows_per_page = parseInt(payload.rows_per_page) || 25;
      await api.put("/config-studio/my-settings", payload);
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-4" data-testid="my-settings">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center">
          <User className="h-5 w-5" strokeWidth={1.6} />
        </div>
        <div>
          <div className="text-xl font-heading font-semibold text-slate-900">{own(user, "Settings")}</div>
          <div className="text-[11px] text-slate-500">Personal preferences — visible only to you.</div>
        </div>
      </header>

      <Section title="Workspace">
        <Row label="Default landing">
          <select value={form.default_landing || ""}
              onChange={(e) => setForm({ ...form, default_landing: e.target.value })}
              data-testid="ms-landing"
              className="border border-slate-200 rounded px-2 py-1 text-[12px] w-56">
            <option value="">— App default —</option>
            {LANDINGS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Row>
        <Row label="Dashboard density">
          <select value={form.dashboard_density || "comfortable"}
              onChange={(e) => setForm({ ...form, dashboard_density: e.target.value })}
              className="border border-slate-200 rounded px-2 py-1 text-[12px] w-56">
            {DENSITIES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Row>
        <Row label="Rows per page">
          <input type="number" value={form.rows_per_page ?? 25}
              onChange={(e) => setForm({ ...form, rows_per_page: e.target.value })}
              data-testid="ms-rows"
              className="border border-slate-200 rounded px-2 py-1 text-[12px] w-24" />
        </Row>
      </Section>

      <Section title="Format">
        <Row label="Date format">
          <select value={form.date_format || "DD/MM/YYYY"}
              onChange={(e) => setForm({ ...form, date_format: e.target.value })}
              className="border border-slate-200 rounded px-2 py-1 text-[12px] w-56">
            {DATE_FORMATS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Row>
        <Row label="Time format">
          <select value={form.time_format || "24h"}
              onChange={(e) => setForm({ ...form, time_format: e.target.value })}
              className="border border-slate-200 rounded px-2 py-1 text-[12px] w-56">
            {TIME_FORMATS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Row>
        <Row label="Currency display">
          <input value={form.currency_display || "INR"}
              onChange={(e) => setForm({ ...form, currency_display: e.target.value })}
              className="border border-slate-200 rounded px-2 py-1 text-[12px] w-28" />
        </Row>
      </Section>

      <Section title="Notifications">
        {[["notification_prefs", "In-app notifications"],
             ["email_notification_prefs", "Email notifications"]].map(([k, label]) => (
          <div key={k} className="mb-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
            <div className="flex flex-wrap gap-2">
              {["approvals", "quotations", "orders", "vendor_registration",
                  "kyc_expiry", "insights_digest"].map((topic) => (
                <label key={topic} className="text-[11px] inline-flex items-center gap-1">
                  <input type="checkbox"
                      checked={!!(form[k]?.[topic] ?? true)}
                      onChange={(e) => setForm({ ...form,
                          [k]: { ...(form[k] || {}), [topic]: e.target.checked } })} />
                  {topic.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          </div>
        ))}
      </Section>

      <div className="sticky bottom-0 bg-white/95 backdrop-blur pt-3 pb-1 border-t border-slate-200 flex justify-end">
        <button onClick={save} disabled={busy}
            data-testid="ms-save"
            className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40 inline-flex items-center gap-1">
          <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : (isTenantUser(user) ? "Save our settings" : "Save my settings")}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="text-[11px] font-heading font-semibold text-slate-900 mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="flex items-center gap-3 text-[12px]">
      <div className="text-slate-500 w-40 shrink-0">{label}</div>
      {children}
    </div>
  );
}
